"use client";

import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import Link from "next/link";
import { clearLastReading, getLastReadingSnapshot, subscribeLastReading } from "@/lib/last-reading";
import { getChatResumeContext } from "@/lib/api/chat";

const COPY = {
  en: {
    welcome: "Welcome back",
    welcomeName: (name: string) => `${name}, welcome back`,
    description:
      "Your kundali is already prepared — continue the consultation from where you left off. No need to re-enter your birth details.",
    continueLabel: "Continue consultation",
    newKundaliLabel: "Start a new kundali",
  },
  hi: {
    welcome: "Phir se swagat hai",
    welcomeName: (name: string) => `${name}, phir se swagat hai`,
    description:
      "Aapki kundali ready hai — pichli consultation wahi se continue karein. Birth details dobara bharne ki zarurat nahi.",
    continueLabel: "Consultation continue karein",
    newKundaliLabel: "Nayi kundali shuru karein",
  },
} as const;

function trackEventLazy(name: string, properties?: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  void import("@/lib/analytics-events").then(({ trackEvent }) => trackEvent(name, properties));
}

/**
 * Returning-visitor resume affordance for chat entry surfaces.
 *
 * Every generated kundali is persisted and resumable at /chat/<id>, but entry
 * pages used to greet returning seekers with an empty birth form. This card
 * reads the last reading from the lib/last-reading external store and leads
 * with "continue your consultation" — a chat-continuation element, so it
 * belongs in the first viewport of a chat-intent page (rule 61). Some pages
 * reserve the same slot for first-time visitors so a stored reading discovered
 * during hydration cannot push the birth form and register as CLS.
 */
export default function ContinueConsultationCard({
  reserveEmpty = true,
  embedded = false,
}: {
  reserveEmpty?: boolean;
  /** Removes the outer card treatment when a parent journey card owns it. */
  embedded?: boolean;
}) {
  const reading = useSyncExternalStore(subscribeLastReading, getLastReadingSnapshot, () => null);
  // The seeker's remembered concern — "your own Jyotishi remembers you". Fetched
  // once per reading.id (keyed on the primitive, never an object, so no re-fetch
  // loop — rule 47), and replaces the generic subhead below when present.
  const [resumeCopy, setResumeCopy] = useState<{ readingId: string; opener: string } | null>(null);
  // chat_resume_card_shown fires once per shown reading id (rule 47: stable
  // string key), AFTER the resume-context fetch settles so its payload can
  // carry `continuity_opener` — the continuity-measurement flag for whether
  // the card showed a memory-derived opener vs the generic copy.
  const shownFiredFor = useRef<string | null>(null);

  const readingId = reading?.id;
  const readingLanguage = reading?.language;
  useEffect(() => {
    if (!readingId) return;
    let active = true;
    const trackShownOnce = (continuityOpener: boolean) => {
      if (shownFiredFor.current === readingId) return;
      shownFiredFor.current = readingId;
      trackEventLazy("chat_resume_card_shown", {
        reading_id: readingId,
        language: readingLanguage,
        continuity_opener: continuityOpener,
      });
    };
    void getChatResumeContext(readingId, readingLanguage)
      .then((ctx) => {
        const hasOpener = Boolean(ctx.has_memory && ctx.opener);
        if (active && hasOpener && ctx.opener) {
          setResumeCopy({ readingId, opener: ctx.opener });
        }
        trackShownOnce(hasOpener);
      })
      .catch(() => {
        /* cold reading / offline — keep the generic copy, never a spinner */
        trackShownOnce(false);
      });
    return () => {
      active = false;
    };
  }, [readingId, readingLanguage]);

  if (!reading) {
    if (!reserveEmpty) return null;
    return (
      <div
        aria-hidden="true"
        data-testid="continue-consultation-card-slot"
        className="mb-4 min-h-36"
      />
    );
  }
  const href = `/chat/${encodeURIComponent(reading.id)}/${reading.language === "hi" ? "?lang=hi" : ""}`;
  const copy = COPY[reading.language];
  const opener = resumeCopy?.readingId === reading.id ? resumeCopy.opener : null;

  return (
    <div
      data-testid="continue-consultation-card"
      data-embedded={embedded || undefined}
      className={
        embedded
          ? ""
          : "mb-4 rounded-xl border border-border-interactive bg-surface-brand-subtle p-4 shadow-card"
      }
    >
      <p className="text-eyebrow font-bold uppercase tracking-eyebrow text-intent-brand">
        {reading.name ? copy.welcomeName(reading.name) : copy.welcome}
      </p>
      <p className="mt-1 text-body leading-relaxed text-text-secondary">
        {opener ?? copy.description}
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Link
          href={href}
          prefetch={false}
          onClick={() =>
            trackEventLazy("chat_resume_clicked", {
              reading_id: reading.id,
              language: reading.language,
            })
          }
          className="inline-flex min-h-11 items-center justify-center rounded-lg bg-surface-brand-strong px-5 text-button font-bold text-text-on-brand"
        >
          {copy.continueLabel}
        </Link>
        <button
          type="button"
          onClick={() => {
            trackEventLazy("chat_resume_dismissed", { reading_id: reading.id });
            clearLastReading();
          }}
          className="inline-flex min-h-11 items-center px-1 text-body-small font-semibold text-text-secondary underline"
        >
          {copy.newKundaliLabel}
        </button>
      </div>
    </div>
  );
}
